/**
 * Transport de secours : trames relayées par le serveur de signaling.
 *
 * ## Pourquoi le chiffrement n'est pas optionnel ici
 *
 * WebRTC chiffre nativement (DTLS-SRTP). Le relais WebSocket, lui, traverse le serveur
 * **en clair** : il faut donc chiffrer nous-mêmes. C'est exactement le rôle de
 * `FrameCodec` — le même que sur le chemin direct, appliqué sans distinction de transport
 * (§6.3, « défense en profondeur »). Cette classe ne voit donc que des trames déjà
 * chiffrées et n'a aucune connaissance du contenu.
 *
 * ## Le contrôle de flux, version bout en bout
 *
 * Sur un `RTCDataChannel`, `bufferedAmount` mesure un tampon local : il dit quand le
 * pair n'absorbe plus assez vite. Un WebSocket n'expose qu'un tampon d'émission local,
 * qui se vide dès que le serveur a reçu la trame — pas quand le destinataire l'a
 * **traitée**. Se fier à lui ferait diverger l'émetteur : il avancerait pendant que le
 * récepteur accumule.
 *
 * On utilise donc une fenêtre d'accusés : chaque trame porte une séquence, le destinataire
 * renvoie `relay-ack`, et l'émetteur compte les octets non acquittés. `waitForDrain()` se
 * résout quand cette fenêtre redescend — c'est le seul signal qui reflète la pression
 * réelle du destinataire, écriture disque comprise.
 */

import { LOW_WATER, type Bytes } from '@attrape/shared';
import type { Transport } from './transport.js';

/** Encode/décode des octets en base64 sans passer par une chaîne UTF-8 (binaire sûr). */
export function bytesToBase64(bytes: Bytes): string {
  let binary = '';
  // Par blocs : `String.fromCharCode(...bytes)` sur un tableau de 64 Ko dépasserait la
  // limite d'arguments de la pile.
  const BLOCK = 0x8000;
  for (let i = 0; i < bytes.length; i += BLOCK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + BLOCK));
  }
  return btoa(binary);
}

export function base64ToBytes(value: string): Bytes {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export interface RelayTransportOptions {
  /** Émet une trame vers le serveur, qui la recopie au pair distant. */
  send: (seq: number, data: string) => boolean;
  /** Émet un accusé de réception à destination de l'émetteur. */
  sendAck: (seq: number) => void;
}

export class RelayTransport implements Transport {
  readonly kind = 'relay' as const;

  private seq = 0;
  private open = true;
  /** Octets émis mais non encore acquittés, par séquence. */
  private readonly unacked = new Map<number, number>();

  private readonly messageHandlers = new Set<(frame: Bytes) => void>();
  private readonly closeHandlers = new Set<() => void>();
  /** Réveille les attentes de drainage quand la fenêtre redescend sous le seuil bas. */
  private drainWaiters: { resolve: () => void; reject: (error: unknown) => void }[] = [];

  /**
   * File d’entrée bornée côté récepteur.
   *
   * On n’acquitte une trame reçue que tant que la file d’entrée reste sous `LOW_WATER`.
   * Tant que l’émetteur n’est pas acquitté, sa trame reste dans `unacked` et son
   * `waitForDrain()` bloque : c’est ce qui borne la mémoire du récepteur à la fenêtre
   * d’émission, au lieu de laisser la file de traitement grossir sans limite. Dès que le
   * traitement libère de la place, on acquitte la trame la plus ancienne en attente.
   */
  private readonly inboundPending: Bytes[] = [];
  private inboundBytes = 0;
  /** Trames reçues en attente d’acquittement (file pleine). */
  private readonly inboundUnacked: { seq: number; frame: Bytes }[] = [];
  private processing = false;

  constructor(private readonly options: RelayTransportOptions) {}

  get isOpen(): boolean {
    return this.open;
  }

  /** Octets non acquittés : la vraie mesure de la pression exercée sur le récepteur. */
  get bufferedAmount(): number {
    let total = 0;
    for (const bytes of this.unacked.values()) total += bytes;
    return total;
  }

  send(frame: Bytes): void {
    if (!this.open) throw new Error('Relais fermé');

    const seq = this.seq;
    this.seq += 1;

    const ok = this.options.send(seq, bytesToBase64(frame));
    if (!ok) {
      // Le signaling est tombé : on refuse plutôt que de compter des octets jamais partis.
      throw new Error('Relais indisponible : signaling fermé');
    }
    this.unacked.set(seq, frame.byteLength);
  }

  waitForDrain(signal?: AbortSignal): Promise<void> {
    if (!this.open) return Promise.reject(new Error('Relais fermé'));
    if (signal?.aborted) {
      return Promise.reject(new DOMException('Transfert annulé', 'AbortError'));
    }
    if (this.bufferedAmount <= LOW_WATER) return Promise.resolve();

    return new Promise<void>((resolve, reject) => {
      const waiter = { resolve, reject };
      const cleanup = (): void => {
        signal?.removeEventListener('abort', onAbort);
        const index = this.drainWaiters.indexOf(waiter);
        if (index !== -1) this.drainWaiters.splice(index, 1);
      };
      const onAbort = (): void => {
        cleanup();
        reject(new DOMException('Transfert annulé', 'AbortError'));
      };

      this.drainWaiters.push(waiter);
      signal?.addEventListener('abort', onAbort, { once: true });
    });
  }

  /** Appelé par le signaling quand un accusé du pair distant arrive. */
  handleAck(seq: number): void {
    if (!this.unacked.delete(seq)) return;
    if (this.bufferedAmount <= LOW_WATER) this.wakeDrainWaiters();
  }

  /**
   * Appelé par le signaling quand une trame relayée arrive du pair distant.
   *
   * L’acquittement n’est émis que tant que la file d’entrée reste sous le seuil bas : tant
   * que le récepteur n’a pas traité les trames précédentes, on retient l’acquittement de
   * la nouvelle trame, ce qui bloque la fenêtre d’émission de l’expéditeur. C’est le
   * backpressure bout en bout annoncé : la file du récepteur ne peut pas grossir au-delà
   * de la fenêtre d’émission.
   */
  handleInbound(seq: number, data: string): void {
    if (!this.open) return;
    let frame: Bytes;
    try {
      frame = base64ToBytes(data);
    } catch {
      // Une charge utile illisible est ignorée plutôt que propagée : elle ne doit pas
      // pouvoir interrompre un transfert en cours.
      return;
    }

    if (this.inboundBytes + frame.byteLength > LOW_WATER) {
      // File d’entrée saturée : on retient l’acquittement. L’émetteur garde la trame dans
      // `unacked` et son `waitForDrain()` bloquera, ce qui est exactement le but.
      this.inboundUnacked.push({ seq, frame });
      return;
    }

    this.options.sendAck(seq);
    this.enqueueInbound(frame);
  }

  private enqueueInbound(frame: Bytes): void {
    this.inboundPending.push(frame);
    this.inboundBytes += frame.byteLength;
    void this.processInbound();
  }

  private async processInbound(): Promise<void> {
    if (this.processing) return;
    this.processing = true;
    try {
      // Tant qu'aucun handler n'est abonné, on garde les trames en file : elles
      // arrivent souvent avant l'installation du `TransferChannel` (pendant l’`await`
      // de `onTransportReady`). Les consommer sans destinataire les perdrait et
      // tronquerait le transfert dès la première trame.
      while (this.open && this.inboundPending.length > 0 && this.messageHandlers.size > 0) {
        const frame = this.inboundPending.shift()!;
        this.inboundBytes -= frame.byteLength;
        for (const handler of this.messageHandlers) handler(frame);
        // Après libération de place, acquitter la plus ancienne trame retenue, puis la
        // pousser en traitement. On ne draine qu’une tranche par place libérée pour ne
        // pas dépasser le seuil.
        const next = this.inboundUnacked.shift();
        if (next) {
          this.options.sendAck(next.seq);
          this.enqueueInbound(next.frame);
        }
      }
    } finally {
      this.processing = false;
    }
  }

  onMessage(handler: (frame: Bytes) => void): () => void {
    this.messageHandlers.add(handler);
    // Un handler vient de s’abonner : rejoue les trames reçues entre-temps, qui
    // étaient restées en file (aucun destinataire n’existait pour les traiter).
    if (this.messageHandlers.size === 1) void this.processInbound();
    return () => this.messageHandlers.delete(handler);
  }

  onClose(handler: () => void): () => void {
    this.closeHandlers.add(handler);
    return () => this.closeHandlers.delete(handler);
  }

  /**
   * Ferme le transport sur erreur fatale (relais rejeté côté serveur).
   *
   * Une trame rejetée par le serveur (`rate_limited`, `payload_too_large`) ne produit
   * jamais de `relay-ack` : sa séquence reste donc dans `unacked` et le prochain
   * `waitForDrain()` resterait pendant indéfiniment. Cette méthode rejette les attentes
   * en cours pour que le transfert échoue explicitement au lieu de se bloquer.
   */
  fail(): void {
    if (!this.open) return;
    this.open = false;
    const waiters = this.drainWaiters.splice(0);
    for (const waiter of waiters) {
      waiter.reject(new Error('Relais fermé par le serveur'));
    }
    for (const handler of this.closeHandlers) handler();
  }

  /** Ferme le transport. Idempotent : un double appel ne renotifie pas. */
  close(): void {
    if (!this.open) return;
    this.open = false;
    this.wakeDrainWaiters();
    for (const handler of this.closeHandlers) handler();
  }

  private wakeDrainWaiters(): void {
    const waiters = this.drainWaiters.splice(0);
    for (const waiter of waiters) waiter.resolve();
  }
}
